import "./styles.css"
import { observer } from "mobx-react-lite"
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import ContentEditable from './ui/ContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import GapToolbarPlugin from "./plugins/ToolbarPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import TreeViewPlugin from './plugins/TreeViewPlugin'
import Placeholder from "./ui/Placeholder"
import DraggableBlockPlugin from "./plugins/DraggableBlockPlugin"
import { useThemeStore } from "theme/ThemeStore"
import LinkPlugin from "./plugins/LinkPlugin"
import GapImagePlugin from "./plugins/GapImagePlugin"
import { useOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore"
import { notNull } from '@gap/utils/utils'
import Box from "@mui/material/Box"
import { useTheme } from "theme/useTheme"
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin"
import useStore from "reusable/mobx-store-utils"
import { ToolbarShowOptions } from './LexicalTypes'
import LexicalRichTextEditorStore from "./LexicalRichTextEditorStore"
import Typography from "@mui/material/Typography"

export enum LexicalMode {
   View = 'view',
   Edit = 'edit'
}
export interface LexicalRichTextEditorProps {
   debugMode?: boolean,
   value?: string,
   mode?: LexicalMode,
   hideResize?: boolean,
   disabled?: boolean,
   label?: string,
   testid?: string,
   toolbarShowOptions?: ToolbarShowOptions[],
   onChange?: (value: string) => void,
   /** Special onChange function that returns the raw HTML of the text editor */
   onChangeRawHtml?: (value: string) => void,
   error?: boolean,
   helperText?: string | undefined,
   fullWidth?: boolean,
   /** Minimum height for the editor in pixels */
   minHeight?: number,
   /** Background color for the editor */
   backgroundColor?: string,
}

export default observer(function LexicalRichTextEditor(props: LexicalRichTextEditorProps) {
   const orgGuardStore = useOrgGuardStore()
   const themeStore = useThemeStore()
   const muiTheme = useTheme()
   const sxBorderColorProps = props.error ? muiTheme.palette.error.main : muiTheme.palette.grey[300]
   const lexicalRichTextEditorStore = useStore(sp => new LexicalRichTextEditorStore(sp), { themeStore, props })

   return (
      <LexicalComposer initialConfig={lexicalRichTextEditorStore.editorConfig}>
         {props.mode === LexicalMode.View ? (
            <RichTextPlugin
               contentEditable={<ContentEditable className="no-class" />}
               placeholder={<Placeholder><></></Placeholder>}
               ErrorBoundary={LexicalErrorBoundary}
            />
         ) : (
            <>
               {props.label && <Typography sx={{ mb: 1 }}>{props.label}</Typography>}
               <Box
                  className={['editor-shell', ...(props.fullWidth ? ['full-width'] : [])].join(' ')}
                  sx={{
                     borderStyle: 'solid',
                     borderWidth: '1px',
                     borderColor: sxBorderColorProps,
                     borderRadius: '8px',
                  }}
               >
                  <Box sx={{ border: 'none', borderBottom: 'solid 1px', borderColor: muiTheme.palette.grey[300], overflow: 'hidden' }}>
                     <Box sx={{ py: '6px' }}>
                        <GapToolbarPlugin orgId={notNull(orgGuardStore.orgId)} show={props.toolbarShowOptions} />
                     </Box>
                  </Box>
                  <Box className="editor-container" data-testid={props.testid} sx={{ border: 'none' }}>
                     <RichTextPlugin
                        contentEditable={
                           <Box
                              className="editor-scroller"
                              sx={{
                                 ...(props.hideResize ? { resize: 'none!important' } : {}),
                                 ...(props.minHeight ? { minHeight: `${props.minHeight}px!important` } : {}),
                                 ...(props.backgroundColor ? { backgroundColor: props.backgroundColor } : {})
                              }}
                           >
                              <Box className="editor" ref={lexicalRichTextEditorStore.editorRef}>
                                 <ContentEditable className="ContentEditable__root" />
                              </Box>
                           </Box>}
                        placeholder={<Placeholder><></></Placeholder>}
                        ErrorBoundary={LexicalErrorBoundary}
                     />
                     <OnChangePlugin onChange={lexicalRichTextEditorStore.onChange} ignoreSelectionChange={false} />
                     <ListPlugin />
                     <LinkPlugin />
                     <GapImagePlugin />
                     {lexicalRichTextEditorStore.floatingAnchorElem && (
                        <>
                           <DraggableBlockPlugin anchorElem={lexicalRichTextEditorStore.floatingAnchorElem} />
                           <FloatingLinkEditorPlugin anchorElem={lexicalRichTextEditorStore.floatingAnchorElem} />
                        </>
                     )}
                  </Box>
                  {props.debugMode && <TreeViewPlugin />}
               </Box>
               {props.helperText ? <Typography color='error' variant='caption' sx={{ mt: 1, ml: '14px' }}>* {props.helperText}</Typography> : null}
            </>
         )}
      </LexicalComposer>
   );
})
